import _ from 'lodash';
import { useMemo, useState } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { Option } from './types';
import {
  useAffiliates,
  useAibList,
  useBrowsers,
  useDevices,
  useDomains,
  useHandlers,
  useOperatingSystems,
} from '../../../views/OfferBlocks/queries';
import { useAffiliateTags } from '../../../views/Affiliates/queries';
import { useOfferSummaries } from '../../../views/Offers/queries';
import { useOfferActionsTable } from '../../../views/OfferActions/queries';

/*
 * Option lists the sections share. Each one was built inline in two or three panes before;
 * they live here so a section and the summary can't drift apart on labels or value formats.
 * React Query caches by key, so several sections calling the same hook cost one request.
 *
 * Value formats differ between criteria on purpose — they are what the backend stores — so a
 * hook's name says which format it produces. Don't merge two that look alike.
 */

type NamedEntity = { name: string; [key: string]: unknown };
type AffiliateRecord = NamedEntity & { networkAffiliateId?: string | number | null };

const byLabel = (a: Option, b: Option) => (a.label > b.label ? 1 : -1);
const nameOptions = (data: NamedEntity[]): Option[] =>
  data.map((item) => ({ label: item.name, value: item.name })).sort(byLabel);

const isNamedEntityArray = (value: unknown): value is NamedEntity[] =>
  Array.isArray(value) &&
  value.every(
    (item) =>
      typeof item === 'object' && item !== null && 'name' in item && typeof (item as NamedEntity).name === 'string'
  );

const tagOptions = (tags: unknown): Option[] =>
  isNamedEntityArray(tags) ? tags.map((tag) => ({ label: `#${tag.name}`, value: `#${tag.name}`, group: 'Tags' })) : [];

export const EMAIL_DOMAIN_OPTIONS: Option[] = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'icloud.com',
  'aol.com',
  'hotmail.com',
].map((domain) => ({ label: domain, value: domain }));

export function useAibListOptions(): Option[] {
  const { data } = useAibList(true, (rows: { listCode: string }[]) =>
    rows.map((row) => ({ label: row.listCode, value: row.listCode })).sort(byLabel)
  );
  return data || [];
}

export function useDeviceOptions(): Option[] {
  const { data } = useDevices(true, nameOptions);
  return data || [];
}

export function useBrowserOptions(): Option[] {
  const { data } = useBrowsers(true, nameOptions);
  return data || [];
}

export function useOperatingSystemOptions(): Option[] {
  const { data } = useOperatingSystems(true, nameOptions);
  return data || [];
}

/**
 * Domains by name, plus the "All Domains" / "Current Domain" pseudo-entries and #tags — the
 * list What If Domain and Previous Registrations pick from.
 */
export function useDomainNameOrTagOptions(): Option[] {
  const { data: domains, isLoading } = useDomains(true, (rows: { name: string }[]) =>
    [
      { label: 'All Domains', value: 'All Domains' },
      { label: 'Current Domain', value: 'Current Domain' },
      ...rows.map((domain) => ({ label: domain.name, value: domain.name })),
    ].sort(byLabel)
  ) as UseQueryResult<Option[]>;
  const { data: tags, isLoading: tagsLoading } = useAffiliateTags('domain');

  return useMemo(() => {
    if (isLoading || tagsLoading || !domains) return [];
    return [...tagOptions(tags), ...domains.map((domain) => ({ ...domain, group: 'Domains' }))];
  }, [domains, tags, isLoading, tagsLoading]);
}

/**
 * Domains labelled "id: name" in DomainID order (flippable), plus #tags — the list
 * Domain-specific Affiliates picks from. Returns the sort state so the section can show a toggle.
 */
export function useDomainByIdOrTagOptions() {
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const { data: domains, isLoading } = useDomains(true, (rows: { name: string; id: number }[]): Option[] =>
    [...rows]
      .sort((a, b) => a.id - b.id)
      .map((domain) => ({ label: `${domain.id}: ${domain.name}`, value: domain.name }))
  ) as UseQueryResult<Option[]>;
  const { data: tags, isLoading: tagsLoading } = useAffiliateTags('domain');

  const options = useMemo(() => {
    if (isLoading || tagsLoading || !domains) return [];
    const ordered = direction === 'desc' ? [...domains].reverse() : domains;
    return [...tagOptions(tags), ...ordered.map((domain) => ({ ...domain, group: 'Domains' }))];
  }, [domains, tags, isLoading, tagsLoading, direction]);

  const toggleDirection = () => setDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  return { options, direction, toggleDirection };
}

/**
 * Affiliates (value: affiliate name, label "networkId: name") plus #tags. `includeNull` adds
 * the "[Null]" pseudo-affiliate that matches traffic with no affiliate at all.
 */
export function useAffiliateNameOrTagOptions({ includeNull = false } = {}): Option[] {
  const { data: affiliates, isLoading } = useAffiliates(true, (rows: AffiliateRecord[]): Option[] =>
    _.orderBy(rows, ['name'], ['asc'])
      .filter(({ networkAffiliateId }: AffiliateRecord) => networkAffiliateId != null)
      .map(({ name, networkAffiliateId }: AffiliateRecord) => ({
        label: `${networkAffiliateId}: ${name}`,
        value: name,
        group: 'Affiliates',
      }))
  ) as UseQueryResult<Option[]>;
  const { data: tags, isLoading: tagsLoading } = useAffiliateTags('affiliate');

  return useMemo(() => {
    if (isLoading || tagsLoading || !affiliates) return [];
    const nullOption: Option[] = includeNull
      ? [{ label: '[Null] (No Affiliate)', value: '[Null]', group: 'Affiliates' }]
      : [];
    return [...nullOption, ...tagOptions(tags), ...affiliates];
  }, [affiliates, tags, isLoading, tagsLoading, includeNull]);
}

/** Affiliates by network affiliate ID (value: the ID as a string) — Quality Score bypass rows. */
export function useAffiliateIdOptions() {
  const { data, isLoading } = useAffiliates(true, (rows: AffiliateRecord[]): Option[] =>
    _.orderBy(rows, ['name'], ['asc']).map(({ name, networkAffiliateId }: AffiliateRecord) => ({
      value: String(networkAffiliateId),
      label: `${networkAffiliateId}: ${name}`,
    }))
  ) as UseQueryResult<Option[]>;
  return { options: data || [], isLoading };
}

/** Offers by ID. `withIdPrefix` labels them "id: name", the way the cap pickers show them. */
export function useOfferOptions({ withIdPrefix = false } = {}) {
  const { data: offers = [], isLoading } = useOfferSummaries();
  const options = useMemo<Option[]>(
    () =>
      (offers as { id: number | string; name?: string }[]).map((offer) => ({
        label: withIdPrefix ? `${offer.id}: ${offer.name || 'Unnamed Offer'}` : offer.name || `Offer ${offer.id}`,
        value: String(offer.id),
      })),
    [offers, withIdPrefix]
  );
  return { options, isLoading };
}

/** Offer actions by ID, labelled the way useOfferOptions labels offers. */
export function useOfferActionOptions({ withIdPrefix = false } = {}) {
  const { data: actions = [], isLoading } = useOfferActionsTable();
  const options = useMemo<Option[]>(
    () =>
      (actions as { id: number | string; title?: string }[]).map((action) => ({
        label: withIdPrefix
          ? `${action.id}: ${action.title || 'Unnamed Action'}`
          : action.title || `Action ${action.id}`,
        value: String(action.id),
      })),
    [actions, withIdPrefix]
  );
  return { options, isLoading };
}

/** Pre-ping handlers, raw (the state stores the whole handler object) and as options. */
export function useHandlerOptions() {
  const { data: handlers = [] } = useHandlers(true, (rows: NamedEntity[]) => _.orderBy(rows, ['name'], ['asc']));
  const options = useMemo<Option[]>(
    () =>
      (handlers as { id: number | string; name?: string }[]).map((handler) => ({
        label: handler.name || `Handler ${handler.id}`,
        value: String(handler.id),
      })),
    [handlers]
  );
  return { handlers: handlers as { id: number | string; name?: string }[], options };
}
